/**
 * اسکیمای FAQPage برای ویجتِ `faq` (صفحه‌ی سوالاتِ متداول).
 *
 * قوانینِ گوگل که اینجا رعایت شده:
 * - FAQPage فقط برای سوال‌هایی است که خودِ سایت نوشته و یک پاسخِ قطعی دارند (پرسش
 *   و پاسخِ کاربران QAPage است). ویجتِ `faq` دقیقاً همین است.
 * - متنِ کاملِ هر سوال و پاسخ باید در صفحه دیده شود. آکاردیون مشکلی ندارد، چون همه‌ی
 *   پاسخ‌ها سمتِ سرور در HTML رندر می‌شوند؛ اسکیما هم از همان `repeatable_fields`ی
 *   ساخته می‌شود که صفحه را می‌سازد، پس متنِ اسکیما و صفحه همیشه یکی است.
 * - در `acceptedAnswer.text` گوگل فقط این تگ‌ها را می‌پذیرد:
 *   h1–h6، br، ol، ul، li، a، p، div، b، strong، i، em. بقیه‌ی تگ‌ها حذف و متنشان
 *   نگه داشته می‌شود؛ از ویژگی‌ها هم فقط `href` لینک (http/https یا مسیرِ داخلی) می‌ماند.
 * - در هر صفحه فقط یک FAQPage، و هر سوال فقط یک بار. سوالِ تکراری حذف می‌شود.
 *
 * `url` و `@id` عمداً ندارد: این ویجت در کامپوننت‌های کلاینت هم رندر می‌شود و آدرسِ
 * صفحه را نمی‌داند؛ گوگل هم برای FAQPage فقط `mainEntity` را لازم دارد. بدونِ `@id`
 * هم با BreadcrumbList همان صفحه ادغام نمی‌شود.
 *
 * گوگل نتیجه‌ی غنیِ FAQ را از ۷ مِی ۲۰۲۶ برای همه‌ی سایت‌ها حذف کرده و مستنداتش را
 * ژوئنِ ۲۰۲۶ برداشته؛ آکاردیونِ FAQ دیگر در نتایجِ گوگل نمی‌آید. اسکیما همچنان
 * schema.orgِ معتبر است، ضرری ندارد، و Bing و خزنده‌های هوش مصنوعی آن را برای فهمِ
 * صفحه می‌خوانند. قوانینِ بالا همان قوانینِ آخرین نسخه‌ی مستنداتِ گوگل است.
 */

const ALLOWED_TAGS = new Set([
	"h1", "h2", "h3", "h4", "h5", "h6",
	"br", "ol", "ul", "li", "a", "p", "div", "b", "strong", "i", "em",
]);

/**
 * تگ‌های درون‌خطی بدونِ فاصله حذف می‌شوند تا `ژو<span>منت</span>` همان «ژومنت»
 * بماند که در صفحه دیده می‌شود؛ تگ‌های بلوکیِ غیرمجاز (جدول و …) با فاصله.
 */
const INLINE_TAGS = new Set([
	"span", "u", "s", "del", "ins", "mark", "small", "sub", "sup", "font",
	"abbr", "code", "kbd", "q", "cite", "time", "label", "bdi", "bdo", "wbr",
]);

/**
 * یک تگِ HTML، با ویژگی‌هایی که داخلِ مقدارشان `>` دارند (`title="a > b"`)؛
 * الگوی ساده‌ی `<[^>]*>` تگ را وسطِ همان مقدار می‌بُرد و بقیه‌اش متن می‌شد.
 */
const TAG_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b(?:[^>"']|"[^"]*"|'[^']*')*>/g;

const decode_spaces = (value) => value.replace(/&nbsp;|&#160;| /g, " ");

const clean_text = (value) => {
	if (typeof value !== "string") return undefined;
	const text = decode_spaces(value.replace(TAG_PATTERN, " ")).replace(/\s+/g, " ").trim();
	return text.length > 0 ? text : undefined;
};

const safe_href = (tag) => {
	const match = tag.match(/\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
	const href = (match?.[1] ?? match?.[2] ?? match?.[3] ?? "").trim();
	if (/^https?:\/\//i.test(href) || /^\/(?!\/)/.test(href)) return href;
	return undefined;
};

/** HTMLِ پاسخ را به زیرمجموعه‌ای که گوگل می‌پذیرد محدود می‌کند. */
const clean_answer_html = (value) => {
	if (typeof value !== "string") return undefined;

	// لینکِ بدونِ href امن (javascript:، mailto:، خالی) باز می‌شود و فقط متنش می‌ماند؛
	// پشته لازم است تا تگِ بسته‌ی همان لینک هم حذف شود.
	const kept_links = [];

	const html = decode_spaces(
		value
			.replace(/<!--[\s\S]*?-->/g, "")
			.replace(/<(script|style|iframe|noscript)\b[\s\S]*?<\/\1>/gi, ""),
	)
		.replace(TAG_PATTERN, (tag, raw_name) => {
			const name = raw_name.toLowerCase();
			const closing = tag.startsWith("</");
			if (!ALLOWED_TAGS.has(name)) return INLINE_TAGS.has(name) ? "" : " ";
			if (name === "br") return closing ? "" : "<br>";
			if (name === "a") {
				if (closing) return kept_links.pop() ? "</a>" : "";
				const href = safe_href(tag);
				kept_links.push(Boolean(href));
				return href ? `<a href="${href.replace(/"/g, "&quot;")}">` : "";
			}
			return closing ? `</${name}>` : `<${name}>`;
		})
		.replace(/\s+/g, " ")
		.replace(/<(p|div|b|strong|i|em|a)\b[^>]*>\s*<\/\1>/g, "")
		.replace(/\s*(<\/?(?:p|div|ol|ul|li|br|h[1-6])>)\s*/g, "$1")
		.trim();

	return clean_text(html) ? html : undefined;
};

/**
 * @param {Array} items  `fields.repeatable_fields` ویجتِ faq (`question`، `answer`)
 * @returns {object|undefined}  اگر هیچ سوالِ معتبری نبود `undefined`، تا اسکیمای خالی چاپ نشود.
 */
export const build_faq_json_ld = (items = []) => {
	const seen = new Set();

	const main_entity = (Array.isArray(items) ? items : [])
		.map((item) => ({
			question: clean_text(item?.question),
			answer: clean_answer_html(item?.answer),
		}))
		.filter(({ question, answer }) => {
			if (!question || !answer || seen.has(question)) return false;
			seen.add(question);
			return true;
		})
		.map(({ question, answer }) => ({
			"@type": "Question",
			name: question,
			acceptedAnswer: {
				"@type": "Answer",
				text: answer,
			},
		}));

	if (main_entity.length === 0) return undefined;

	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		inLanguage: "fa-IR",
		mainEntity: main_entity,
	};
};
